import type { MetricDefinition } from '../types/dashboard';

const MEMBER_FILTERS = ['Date range', 'Territory', 'AOP', 'FBC / AD', 'Acquisition source'];
const REG_FILTERS = [...MEMBER_FILTERS, 'Sport', 'Program type', 'Program season', 'Program year'];
const RETENTION_FILTERS = ['Territory', 'AOP', 'FBC / AD', 'Sport', 'Program type', 'Base & comparison season'];

export const metricDefinitions: MetricDefinition[] = [
  {
    id: 'members',
    name: 'New Members',
    definition:
      'Members newly acquired (added to the i9 Sports database within an AOP) during the selected date range — not the total existing member database. Potential and current customers whose details were collected via the website, a paid ad or local marketing.',
    calculation:
      'COUNT DISTINCT member per territory, added within the selected date range. Franchisee count: a person attributed to two territories counts once in each, and network totals add the territories together.',
    grain: 'Member × Territory',
    dateBasis: 'Date the member was added (acquisition date)',
    filters: MEMBER_FILTERS,
    status: 'Pending validation',
    note: 'Exact member identifier and "date added" field to be confirmed. Never filtered by sport, program, season or year — a member is only tied to a program through a qualifying registration.',
  },
  {
    id: 'registrations',
    name: 'Registrations',
    definition: 'Program enrollments (unique program purchases), not payment transactions. One member can produce several registrations.',
    calculation: 'COUNT of enrollments made within the selected date range by members added in that same range.',
    grain: 'Enrollment',
    dateBasis: 'Registration date, inside the selected range',
    filters: REG_FILTERS,
    status: 'Proposed',
    note: 'Registrations by members added before the range are excluded by design so the funnel stays date-range specific.',
  },
  {
    id: 'conversion',
    name: 'Conversion Rate',
    definition: 'Share of members added in the selected date range who made at least one qualifying registration in that same range.',
    calculation: 'DISTINCT members added in range AND registered in range ÷ DISTINCT members added in range.',
    grain: 'Unique member × Territory',
    dateBasis: 'Acquisition date and first registration both inside the selected range',
    filters: REG_FILTERS,
    status: 'Proposed',
    note: 'Unique-member conversion is kept separate from Registrations per Member. A parent registering three children counts as one converted member. All-time conversion is not mixed in.',
  },
  {
    id: 'regsPerMember',
    name: 'Registrations per Member',
    definition: 'Average registrations per converted member. Separates a parent signing up three kids from three separate families.',
    calculation: 'Registrations ÷ DISTINCT members who contributed those registrations.',
    grain: 'Ratio',
    dateBasis: 'Same date range as Conversion Rate',
    filters: REG_FILTERS,
    status: 'Proposed',
  },
  {
    id: 'revenue',
    name: 'Revenue',
    definition: 'Registration revenue from the registrations counted above.',
    calculation: 'SUM of registration revenue for in-range registrations by members added in range. Gross vs. net of discounts and refunds still to be decided.',
    grain: 'Enrollment',
    dateBasis: 'Registration date',
    filters: REG_FILTERS,
    status: 'Pending validation',
    note: 'Subject to reconciliation with existing Power BI revenue figures.',
  },
  {
    id: 'seasonRetention',
    name: 'Season Retention',
    definition: 'Share of a base season’s unique participants who also registered in the comparison season.',
    calculation: 'Unique participants registered in both seasons ÷ unique participants in the base season. Participants with several registrations in a season count once.',
    grain: 'Participant, deduplicated within season',
    dateBasis: 'Season and year assigned to the registered program — not the registration (purchase) date',
    filters: RETENTION_FILTERS,
    status: 'Proposed',
    note: 'Requires a program season/year field on each registration. Participant identifier pending validation.',
  },
  {
    id: 'yoyRetention',
    name: 'YOY Retention',
    definition: 'Participant cohort retention from a base season to the same season one year later, with an optional “any program within 12 months” variant.',
    calculation: 'Unique base-season participants who registered in the comparison period ÷ unique base-season participants.',
    grain: 'Participant, deduplicated within season',
    dateBasis: 'Program season and year',
    filters: RETENTION_FILTERS,
    status: 'Pending validation',
    note: 'Comparison window (same season next year vs. any program within 12 months) needs confirmation.',
  },
  {
    id: 'aop',
    name: 'AOP (Area of Play)',
    definition: 'The 10–15 minute drive time around a venue where a program is marketed. Named after a city, neighborhood, county or similar.',
    calculation: 'ZIP codes associated with a listed Area of Play in i9 Sports Connect. Members and registrations roll up to an AOP by ZIP code.',
    grain: 'ZIP code → AOP → Territory',
    dateBasis: 'Current ZIP assignment in Connect',
    filters: ['Territory', 'AOP', 'FBC / AD'],
    status: 'Confirmed',
    note: 'Handling of historical ZIP reassignments still to be confirmed.',
  },
  {
    id: 'acquisitionSource',
    name: 'Acquisition Source',
    definition: 'How a member was acquired: Digital (website form or paid ad) or Local (events, schools, field marketing).',
    calculation: 'Assigned from the attribution channel on the member record. Registrations and revenue inherit the registering member’s source.',
    grain: 'Member',
    dateBasis: 'Acquisition date',
    filters: MEMBER_FILTERS,
    status: 'Pending validation',
    note: 'Classification rules remain subject to data validation. Not applied to retention.',
    supporting: true,
  },
  {
    id: 'networkAverage',
    name: 'Network Avg per Active Territory',
    definition: 'Benchmark used for variance and status. AOP views divide by active AOPs instead.',
    calculation:
      'Monthly: network total for the month ÷ active territories that month (e.g. Sep ’26 new members ÷ territories active in Sep ’26). KPI cards show the latest completed month. Period figures are the mean of the monthly averages, never a 13-month sum. Territory comparisons use the territory’s average per active month vs this monthly benchmark over the same months. Rates use network totals.',
    grain: 'Territory or AOP × Month',
    dateBasis: 'Selected date range; a territory is compared only on months it was active',
    filters: REG_FILTERS,
    status: 'Proposed',
    note: 'Active-territory denominator pending validation; the prototype treats a territory as active from its launch month.',
    supporting: true,
  },
  {
    id: 'performanceStatus',
    name: 'Performance Status',
    definition: 'Rule-based comparison to the network average for the selected metric. No scoring model.',
    calculation:
      'Counts compare the territory’s average per active month with the network monthly average per active territory. Counts (members, registrations, revenue): Below ≥10% under, Priority review ≥25% under, Above ≥10% over. Conversion: 3 pp / 6 pp. Registrations per member: 5% / 12%. Ramping: launched < 12 months before range end.',
    grain: 'Territory or AOP',
    dateBasis: 'Selected date range',
    filters: REG_FILTERS,
    status: 'Proposed',
    note: 'Prototype thresholds for business review.',
    supporting: true,
  },
];
